// /src/components/tools/memberProfileCard/PerkThemeGlassDropsCanvas.jsx
// WebGL rain-on-glass — Curtains-style shader.
// Deltas: 2 layers, weaker frost, radioactive green beads OR plain wet-glass highlight,
// procedural fallback texture when no media.

import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { isHlsMedia } from '../../../utils/memberProfileThemeDisplay';

const VERT = `
attribute vec2 aPos;
varying vec2 vTextureCoord;
void main() {
  vTextureCoord = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

// Fragment = demo shader (Layer + main), with intentional deltas:
//   - 2 Layer() calls, weaker frost
//   - radioactive bead tint OR plain wet-glass bead highlight (readable on dark fallback)

const FRAG = `
#ifdef GL_ES
precision mediump float;
#endif

#define PI2 6.28318530718
#define PI 3.14159265359
#define S(a,b,n) smoothstep(a,b,n)

varying vec2 vTextureCoord;

uniform float uTime;
uniform float uRadioactive;
uniform float uDensity;
uniform sampler2D dispImage;

float N12(vec2 p){
  p = fract(p * vec2(123.34, 345.45));
  p += dot(p, p + 34.345);
  return fract(p.x * p.y);
}

vec4 Layer(vec2 uv0, float t, float grid){
  vec2 asp = vec2(2., 1.);

  vec2 uv1 = uv0 * grid * asp;

  uv1.y += t * .25;

  vec2 gv = fract(uv1) - .5;
  vec2 id = floor(uv1);

  float n = N12(id);

  t+= n * PI2;

  float w = uv0.y * 10.;
  float x = (n - .5) * .8;
  x += (.4 - abs(x)) * sin(3. * w) * pow(sin(w), 6.) * .45;
  float y = -sin(t + sin(t + sin(t) * .5)) * (.5 - .06);
  y -= (gv.x - x) * (gv.x - x);

  vec2 dropPos = (gv - vec2(x, y)) / asp;
  float drop = S(.03, .02, length(dropPos));

  vec2 trailPos = (gv - vec2(x, t * .25)) / asp;
  trailPos.y = (fract(trailPos.y * 8.) - .5) / 8.;
  float trail = S(.02, .015, length(trailPos));

  float fogTrail = S(-.05, .05, dropPos.y);

  fogTrail *= S(.5, y, gv.y);
  trail *= fogTrail;
  fogTrail *= S(.03, .015, abs(dropPos.x));

  vec2 off = drop * dropPos + trail * trailPos;
  // w = bead mask for tint / wet highlight (drop + trail only, not fog sheet)
  float bead = clamp(drop + trail * 0.65, 0.0, 1.0);

  return vec4(off, fogTrail, bead);
}

void main() {
  float dist = 5.;
  float blurSize = 5.;
  float t = mod(uTime * .03, 7200.);
  float dens = clamp(uDensity, 0.0, 1.0);
  // Same drop style; density only changes grid frequency (+ 2nd layer weight)
  float grid = mix(1.35, 3.0, dens);

  vec2 uv = vTextureCoord;

  vec4 drops = Layer(uv, t, grid);
  float layer2 = smoothstep(0.22, 0.82, dens);
  drops += Layer(uv * 1.35 + 1.54, t, grid) * layer2;

  float blur = blurSize * 7. * (1. - drops.z);

  vec4 col = vec4(0.);
  float a = N12(uv) * PI2;

  // Demo used 0.0005 — lower = less global frost (buée), without touching fogTrail
  blur *= .00007;
  uv += drops.xy * dist;

  for(int n = 0; n < 32; n++){
    vec2 off = vec2(sin(a), cos(a)) * blur;
    float d = fract(sin((float(n) + 1.) * 546.) * 5424.);
    d = sqrt(d);
    off *= d;
    col += texture2D(dispImage, uv + off);
    a++;
  }

  col /= 32.;

  float bead = clamp(drops.w, 0.0, 1.0);
  if (uRadioactive > 0.5) {
    vec3 green = vec3(0.3, 1.1, 0.22);
    col.rgb = mix(col.rgb, col.rgb * green + vec3(0.05, 0.2, 0.04), bead * 0.8);
  } else {
    // Plain drip: cool wet specular so beads stay visible on dark / flat plates
    vec3 wet = vec3(0.62, 0.78, 0.92);
    col.rgb = mix(col.rgb, col.rgb * 1.12 + wet * 0.28, bead * 0.72);
    col.rgb += vec3(0.03, 0.04, 0.055) * drops.z * 0.45;
  }

  gl_FragColor = col;
}
`;

function compile(gl, type, src) {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const msg = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(msg || 'Shader compile failed');
  }
  return sh;
}

function createProgram(gl, vsSrc, fsSrc) {
  const vs = compile(gl, gl.VERTEX_SHADER, vsSrc);
  const fs = compile(gl, gl.FRAGMENT_SHADER, fsSrc);
  const prog = gl.createProgram();
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(prog) || 'Program link failed');
  }
  return prog;
}

function loadImageElement(url, useCrossOrigin) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (useCrossOrigin) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('texture load failed'));
    img.src = url;
  });
}

/**
 * WebGL needs a CORS-clean bitmap. Static CDN often blocks canvas/WebGL reads,
 * so cross-origin theme URLs go through the same-origin proxy used by crop.
 * @returns {Promise<{ img: HTMLImageElement, revoke?: () => void }>}
 */
async function loadImageForTexture(url) {
  const src = String(url || '').trim();
  if (!src) throw new Error('missing media url');

  if (src.startsWith('blob:') || src.startsWith('data:')) {
    const img = await loadImageElement(src, false);
    return { img };
  }

  let parsed;
  try {
    parsed = new URL(src, window.location.href);
  } catch (_) {
    throw new Error('invalid media url');
  }

  if (parsed.origin === window.location.origin) {
    const img = await loadImageElement(src, false);
    return { img };
  }

  const proxyPaths = [
    '/api/media/fetch-for-canvas',
    '/api/admin/fetch-media-for-canvas',
  ];
  let lastErr = null;
  for (let i = 0; i < proxyPaths.length; i += 1) {
    try {
      const { data } = await axios.get(proxyPaths[i], {
        params: { url: src },
        responseType: 'blob',
        withCredentials: true,
      });
      const blobUrl = URL.createObjectURL(data);
      try {
        const img = await loadImageElement(blobUrl, false);
        return {
          img,
          revoke: () => URL.revokeObjectURL(blobUrl),
        };
      } catch (err) {
        URL.revokeObjectURL(blobUrl);
        throw err;
      }
    } catch (err) {
      lastErr = err;
    }
  }

  // Last resort: direct CORS (works if CDN sends ACAO)
  try {
    const img = await loadImageElement(src, true);
    return { img };
  } catch (_) {
    throw lastErr || new Error('texture load failed');
  }
}

/** Procedural dark glass plate when no theme media — drips still refract. */
function createFallbackDispCanvas() {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 512;
  const ctx = c.getContext('2d');
  if (!ctx) return c;

  const base = ctx.createLinearGradient(0, 0, 420, 512);
  base.addColorStop(0, '#0c1824');
  base.addColorStop(0.35, '#1a3348');
  base.addColorStop(0.65, '#142838');
  base.addColorStop(1, '#0e1c2a');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, 512, 512);

  const lobes = [
    [110, 380, 240, 'rgba(90, 150, 190, 0.42)'],
    [320, 160, 210, 'rgba(120, 175, 210, 0.34)'],
    [400, 420, 180, 'rgba(70, 130, 165, 0.36)'],
    [180, 120, 160, 'rgba(150, 195, 220, 0.26)'],
    [250, 280, 120, 'rgba(200, 220, 235, 0.14)'],
  ];
  for (let i = 0; i < lobes.length; i += 1) {
    const [x, y, r, color] = lobes[i];
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, color);
    g.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 512, 512);
  }

  // Soft diagonal highlight band (extra contrast for refraction)
  const band = ctx.createLinearGradient(40, 480, 480, 40);
  band.addColorStop(0, 'rgba(0, 0, 0, 0)');
  band.addColorStop(0.45, 'rgba(180, 210, 235, 0.12)');
  band.addColorStop(0.55, 'rgba(220, 235, 245, 0.16)');
  band.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = band;
  ctx.fillRect(0, 0, 512, 512);

  // Fine grain so refraction has something to chew on
  const imgData = ctx.getImageData(0, 0, 512, 512);
  const data = imgData.data;
  for (let i = 0; i < data.length; i += 12) {
    const n = ((i * 17) % 17) - 8;
    data[i] = Math.max(0, Math.min(255, data[i] + n));
    data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + n));
    data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + n));
  }
  ctx.putImageData(imgData, 0, 0);
  return c;
}

function createTexture(gl, img) {
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
  return tex;
}

/**
 * @param {{
 *   mediaUrl?: string,
 *   mediaType?: string,
 *   radioactive?: boolean,
 *   density?: number,
 * }} props
 * density 0–100 (grid frequency only; same drop style)
 */
export default function PerkThemeGlassDropsCanvas({
  mediaUrl,
  mediaType,
  radioactive = false,
  density = 100,
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const densityRef = useRef(density);
  const src = String(mediaUrl || '').trim();
  const skipHls = Boolean(src) && isHlsMedia(src, mediaType);

  useEffect(() => {
    const n = Number(density);
    densityRef.current = Number.isFinite(n)
      ? Math.max(0, Math.min(100, n))
      : 100;
  }, [density]);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas || skipHls) return undefined;

    const reduceMotion =
      typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return undefined;

    const gl = canvas.getContext('webgl', {
      alpha: false,
      antialias: false,
      premultipliedAlpha: true,
      preserveDrawingBuffer: false,
    });
    if (!gl) return undefined;

    let cancelled = false;
    let rafId = null;
    let resizeObserver = null;
    let texture = null;
    let program = null;
    let buffer = null;
    let revokeBlob = null;
    let ready = false;
    let uTime = 0;
    let viewW = 0;
    let viewH = 0;
    const locs = {};

    canvas.classList.remove('is-ready');

    const applySize = () => {
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      if (w === viewW && h === viewH && canvas.width > 0) return;
      viewW = w;
      viewH = h;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    const frame = () => {
      if (cancelled || !ready || !program || !texture) return;
      if (viewW < 2 || viewH < 2) {
        applySize();
        rafId = window.requestAnimationFrame(frame);
        return;
      }

      // Same as Curtains demo: plane.uniforms.time.value++
      uTime += 1;

      gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.enableVertexAttribArray(locs.aPos);
      gl.vertexAttribPointer(locs.aPos, 2, gl.FLOAT, false, 0, 0);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.uniform1i(locs.dispImage, 0);
      gl.uniform1f(locs.uTime, uTime);
      gl.uniform1f(locs.uRadioactive, radioactive ? 1 : 0);
      gl.uniform1f(locs.uDensity, densityRef.current / 100);

      gl.drawArrays(gl.TRIANGLES, 0, 6);
      canvas.classList.add('is-ready');
      rafId = window.requestAnimationFrame(frame);
    };

    (async () => {
      try {
        program = createProgram(gl, VERT, FRAG);
        buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(
          gl.ARRAY_BUFFER,
          new Float32Array([
            -1, -1, 1, -1, -1, 1,
            -1, 1, 1, -1, 1, 1,
          ]),
          gl.STATIC_DRAW,
        );

        locs.aPos = gl.getAttribLocation(program, 'aPos');
        locs.dispImage = gl.getUniformLocation(program, 'dispImage');
        locs.uTime = gl.getUniformLocation(program, 'uTime');
        locs.uRadioactive = gl.getUniformLocation(program, 'uRadioactive');
        locs.uDensity = gl.getUniformLocation(program, 'uDensity');

        let sourceImg;
        if (src) {
          const loaded = await loadImageForTexture(src);
          if (cancelled) {
            if (loaded.revoke) loaded.revoke();
            return;
          }
          revokeBlob = loaded.revoke || null;
          sourceImg = loaded.img;
        } else {
          sourceImg = createFallbackDispCanvas();
        }

        texture = createTexture(gl, sourceImg);
        if (revokeBlob) {
          revokeBlob();
          revokeBlob = null;
        }
        ready = true;
        applySize();

        if (typeof ResizeObserver === 'function') {
          resizeObserver = new ResizeObserver(() => {
            applySize();
          });
          resizeObserver.observe(wrap);
        }
        rafId = window.requestAnimationFrame(frame);
      } catch (err) {
        console.warn('[PerkThemeGlassDropsCanvas]', err?.message || err);
        canvas.classList.remove('is-ready');
      }
    })();

    return () => {
      cancelled = true;
      ready = false;
      if (rafId != null) window.cancelAnimationFrame(rafId);
      if (resizeObserver) resizeObserver.disconnect();
      if (revokeBlob) revokeBlob();
      if (texture) gl.deleteTexture(texture);
      if (buffer) gl.deleteBuffer(buffer);
      if (program) gl.deleteProgram(program);
    };
  }, [src, mediaType, radioactive, skipHls]);

  if (skipHls) return null;

  return (
    <div
      ref={wrapRef}
      className={[
        'perk-theme-overlay__layer',
        'perk-theme-overlay__layer--glass-webgl',
        radioactive ? 'perk-theme-overlay__layer--glass-radioactive' : '',
      ].filter(Boolean).join(' ')}
      aria-hidden
    >
      <canvas ref={canvasRef} className="perk-theme-overlay__glass-canvas" />
    </div>
  );
}
